import { IUseLocalStorage } from '@monofront/types';
import { useMemo } from 'react';
import { DeliveryMethodCategoryEnum } from '../constants';
import { IAuthState, useLocalOrRemoteCustomer } from '../contexts';
import { useFeatureIsOn } from '../contexts/growthbook';
import { DeliveryBarMachineContextShippingMethod } from '../stateMachines/deliveryBar/types';
import { usePreferredShippingMethodWithConfigurations } from './usePreferredShippingMethodWithConfigurations';

export const getIsShippingFreshMethodEligible = (
  shippingMethod: DeliveryBarMachineContextShippingMethod | undefined,
): boolean => {
  return (
    shippingMethod?.configurations?.some(
      (config) => config.category === DeliveryMethodCategoryEnum.FRESH,
    ) ?? false
  );
};

export const getIsShippingUltraFreshMethodEligible = (
  shippingMethod: DeliveryBarMachineContextShippingMethod | undefined,
): boolean => {
  return (
    shippingMethod?.configurations?.some(
      (config) => config.category === DeliveryMethodCategoryEnum.ULTRA_FRESH,
    ) ?? false
  );
};

type UseDeliveryMethodEligibilityProps = {
  useAuthentication: () => IAuthState;
  useLocalStorage: IUseLocalStorage;
};

export const useDeliveryMethodEligibility = ({
  useAuthentication,
  useLocalStorage,
}: UseDeliveryMethodEligibilityProps) => {
  const isAddressFreshMethodEligible = useFeatureIsOn('fresh_eligibility');
  const isAddressUltraFreshMethodEligible = useFeatureIsOn(
    'ultra_fresh_eligibility',
  );

  const { resolvedCustomer } = useLocalOrRemoteCustomer({
    useAuthentication,
    useLocalStorage,
  });

  const shippingMethodWithConfigurations =
    usePreferredShippingMethodWithConfigurations({
      address: resolvedCustomer?.address ?? null,
      preferredShippingMethod:
        resolvedCustomer?.preferredShippingMethod ?? null,
    });

  const isShippingFreshMethodEligible = useMemo(
    () => getIsShippingFreshMethodEligible(shippingMethodWithConfigurations),
    [shippingMethodWithConfigurations],
  );

  const isShippingUltraFreshMethodEligible = useMemo(
    () =>
      getIsShippingUltraFreshMethodEligible(shippingMethodWithConfigurations),
    [shippingMethodWithConfigurations],
  );

  return useMemo(
    () => ({
      isShippingFreshMethodEligible,
      isShippingUltraFreshMethodEligible,
      isAddressFreshMethodEligible,
      isAddressUltraFreshMethodEligible,
    }),
    [
      isShippingFreshMethodEligible,
      isShippingUltraFreshMethodEligible,
      isAddressFreshMethodEligible,
      isAddressUltraFreshMethodEligible,
    ],
  );
};
